import React, { Context, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useUserState } from './user-state.provider.ts';
import { usePageActivationRefresh } from '../hooks/usePageActivationRefresh';

interface PurchaseStatusContextType {
    ownedVideoIds: Set<string>;
    isPurchaseLoading: boolean;
    isVideoOwned: (videoId?: string | null) => boolean;
    refreshEntitlements: () => Promise<void>;
}

interface PurchaseStatusProviderProps {
    children: JSX.Element | JSX.Element[];
}

const PurchaseStatusContext: Context<PurchaseStatusContextType> = createContext<PurchaseStatusContextType>(null!);
const EMPTY_VIDEO_IDS = new Set<string>();

const PurchaseStatusProvider = ({ children }: PurchaseStatusProviderProps) => {
    const { isLoggedIn, isUserStateLoading, getUserInfo } = useUserState();
    const userInfo = getUserInfo();
    const authKey = isLoggedIn ? userInfo?.token ?? '' : '';
    const [ownedVideoIds, setOwnedVideoIds] = useState<Set<string>>(new Set());
    const [isPurchaseLoading, setIsPurchaseLoading] = useState(false);
    const [entitlementAuthKey, setEntitlementAuthKey] = useState(authKey);
    const isMountedRef = useRef(true);
    const refreshPromiseRef = useRef<{ authKey: string; promise: Promise<void> } | null>(null);
    const trailingRefreshPromiseRef = useRef<{ authKey: string; promise: Promise<void> } | null>(null);
    const authKeyRef = useRef(authKey);
    // Invalidate old-account requests before effects run so stale entitlements are never exposed.
    if (authKeyRef.current !== authKey) {
        authKeyRef.current = authKey;
        refreshPromiseRef.current = null;
        trailingRefreshPromiseRef.current = null;
    }

    const fetchEntitlements = useCallback(async (requestedAuthKey: string) => {
        const requestUser = getUserInfo();
        if (!isLoggedIn || !requestedAuthKey || requestUser?.token !== requestedAuthKey) {
            if (isMountedRef.current) {
                setEntitlementAuthKey(requestedAuthKey);
                setOwnedVideoIds(new Set());
                setIsPurchaseLoading(false);
            }
            return;
        }

        if (isMountedRef.current) {
            setIsPurchaseLoading(true);
        }

        try {
            const { fetchVideoEntitlements } = await import('../services/purchase.service');
            const response = await fetchVideoEntitlements(requestedAuthKey);
            if (authKeyRef.current !== requestedAuthKey) {
                return;
            }

            if (isMountedRef.current) {
                const nextIds = new Set(response.VideoIds ?? []);
                setEntitlementAuthKey(requestedAuthKey);
                setOwnedVideoIds((currentIds) => {
                    const isUnchanged = currentIds.size === nextIds.size
                        && Array.from(currentIds).every((videoId) => nextIds.has(videoId));
                    return isUnchanged ? currentIds : nextIds;
                });
            }
        } catch {
            if (isMountedRef.current && authKeyRef.current === requestedAuthKey) {
                setEntitlementAuthKey(requestedAuthKey);
                setOwnedVideoIds(new Set());
            }
        } finally {
            if (isMountedRef.current && authKeyRef.current === requestedAuthKey) {
                setIsPurchaseLoading(false);
            }
        }
    }, [getUserInfo, isLoggedIn]);

    const startEntitlementRefresh = useCallback((requestedAuthKey: string): Promise<void> => {
        const refreshPromise = fetchEntitlements(requestedAuthKey);
        refreshPromiseRef.current = { authKey: requestedAuthKey, promise: refreshPromise };
        void refreshPromise.finally(() => {
            if (refreshPromiseRef.current?.promise === refreshPromise) {
                refreshPromiseRef.current = null;
            }
        });
        return refreshPromise;
    }, [fetchEntitlements]);

    const refreshEntitlements = useCallback((): Promise<void> => {
        const inFlightRefresh = refreshPromiseRef.current;
        if (inFlightRefresh?.authKey !== authKey) {
            return startEntitlementRefresh(authKey);
        }

        if (trailingRefreshPromiseRef.current?.authKey === authKey) {
            return trailingRefreshPromiseRef.current.promise;
        }

        const requestedAuthKey = authKey;
        const trailingPromise = inFlightRefresh.promise
            .catch(() => undefined)
            .then(() => {
                if (trailingRefreshPromiseRef.current?.promise === trailingPromise) {
                    trailingRefreshPromiseRef.current = null;
                }
                if (authKeyRef.current === requestedAuthKey) {
                    return startEntitlementRefresh(requestedAuthKey);
                }
                return undefined;
            });
        trailingRefreshPromiseRef.current = { authKey: requestedAuthKey, promise: trailingPromise };
        void trailingPromise.finally(() => {
            if (trailingRefreshPromiseRef.current?.promise === trailingPromise) {
                trailingRefreshPromiseRef.current = null;
            }
        });
        return trailingPromise;
    }, [authKey, startEntitlementRefresh]);

    useEffect(() => {
        isMountedRef.current = true;

        const syncEntitlements = async () => {
            if (isUserStateLoading) {
                return;
            }

            await refreshEntitlements();
        };

        syncEntitlements();

        return () => {
            isMountedRef.current = false;
        };
    }, [isUserStateLoading, refreshEntitlements]);

    usePageActivationRefresh(
        isLoggedIn && !isUserStateLoading,
        refreshEntitlements
    );

    const currentOwnedVideoIds = entitlementAuthKey === authKey ? ownedVideoIds : EMPTY_VIDEO_IDS;
    const isVideoOwned = useCallback((videoId?: string | null) => {
        if (!videoId) {
            return false;
        }
        return currentOwnedVideoIds.has(videoId);
    }, [currentOwnedVideoIds]);

    const value = useMemo(() => ({
        ownedVideoIds: currentOwnedVideoIds,
        isPurchaseLoading: !!authKey && (entitlementAuthKey !== authKey || isPurchaseLoading),
        isVideoOwned,
        refreshEntitlements
    }), [authKey, currentOwnedVideoIds, entitlementAuthKey, isPurchaseLoading, isVideoOwned, refreshEntitlements]);

    return (
        <PurchaseStatusContext.Provider value={value}>
            {children}
        </PurchaseStatusContext.Provider>
    );
};

export const usePurchaseStatus = () => {
    return useContext(PurchaseStatusContext);
};

export default PurchaseStatusProvider;
